// Phase I3.C — Super Acca canonical page (Direction A).
//
// Replaces the older Bet Builder card. Renders the 12-panel command
// centre per the prototype layout: MatchCommandHeader → TierStrip →
// IntelligenceVerdict → IntelligenceStack → MarketCategorySelector →
// (one of RefereeDNA / PlayerDNA / Corners / "ALL") → ScorelineMatrix
// → AdversarialReview → AgentDebate → BuildYourOwn.
//
// Direction A only. Direction B/C placeholders ship beside this file but
// are not wired to a toggle yet (deferred to I5). User preference is
// persisted under localStorage `strikza:superacca:direction` so the
// toggle can be wired without breaking the route.

import { useEffect, useMemo, useState } from "react";
import { useConversionTriggerPump } from "../../free-tier/useConversionTriggerPump.ts";
import { useSuperAccaToday } from "../../super-acca/useSuperAccaToday.ts";
import { useUserTier } from "../../auth/useUserTier.ts";
import { PaywallCard } from "../../components/PaywallCard.tsx";
import { SuperAccaPageDirectionB } from "./SuperAccaPageDirectionB.tsx";
import { SuperAccaPageDirectionC } from "./SuperAccaPageDirectionC.tsx";
import { AdversarialReview } from "./panels/AdversarialReview.tsx";
import { AgentDebate } from "./panels/AgentDebate.tsx";
import { BuildYourOwn } from "./panels/BuildYourOwn.tsx";
import { CornersIntelPanel } from "./panels/CornersIntelPanel.tsx";
import { IntelligenceStack } from "./panels/IntelligenceStack.tsx";
import { IntelligenceVerdict } from "./panels/IntelligenceVerdict.tsx";
import { MarketCategorySelector } from "./panels/MarketCategorySelector.tsx";
import type { MarketCategoryFilter } from "./panels/MarketCategorySelector.tsx";
import { MatchCommandHeader } from "./panels/MatchCommandHeader.tsx";
import { PlayerDNAPanel } from "./panels/PlayerDNAPanel.tsx";
import { RefereeDNAPanel } from "./panels/RefereeDNAPanel.tsx";
import { ScorelineMatrix } from "./panels/ScorelineMatrix.tsx";
import { TierStrip } from "./panels/TierStrip.tsx";
import { DoctrineSection } from "../../components/DoctrineSection.tsx";
import { useDoctrinePicks } from "../../doctrine/useDoctrinePicks.ts";

const DIRECTION_KEY = "strikza:superacca:direction";

function readDirection(): "A" | "B" | "C" {
  if (typeof window === "undefined") return "A";
  try {
    const v = window.localStorage.getItem(DIRECTION_KEY);
    if (v === "B" || v === "C") return v;
  } catch {
    /* ignore */
  }
  return "A";
}

export function SuperAccaPage(): JSX.Element {
  const [direction] = useState<"A" | "B" | "C">(() => readDirection());
  const { isPro, loading: tierLoading } = useUserTier();
  // Persist default once so future I5 toggle reads the slot reliably.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (!window.localStorage.getItem(DIRECTION_KEY)) {
        window.localStorage.setItem(DIRECTION_KEY, direction);
      }
    } catch {
      /* ignore */
    }
  }, [direction]);

  // Phase I5.2 — Pro tier gate. Anon / free-tier callers see the
  // PaywallCard. The paystack-checkout Edge fn (Phase H) does the rest.
  if (tierLoading) {
    return (
      <div
        className="sa-root"
        style={{
          background: "var(--sa-bg-base)",
          minHeight: "var(--vh-full)",
          color: "var(--sa-fg-secondary)",
          fontFamily: "var(--sa-font-mono)",
          fontSize: 12,
          letterSpacing: "0.18em",
          padding: 40,
        }}
      >
        LOADING TIER…
      </div>
    );
  }
  if (!isPro) {
    return (
      <div
        className="sa-root"
        style={{
          background: "var(--sa-bg-base)",
          minHeight: "var(--vh-full)",
          padding: "60px 24px",
          fontFamily: "var(--sa-font-body)",
          color: "var(--sa-fg-primary)",
        }}
      >
        <div style={{ maxWidth: 560, margin: "0 auto" }}>
          <PaywallCard
            requiredTier="sika_pro"
            featureName="Super Acca Bet Builder"
            pitch="Daily 2-3 leg same-game build with the full intelligence stack: contradiction engine, adversarial review, sharp-money read, scoreline matrix, DNA panels, and Build-Your-Own scoring. Pro+ only."
          />
        </div>
      </div>
    );
  }

  if (direction === "B") return <SuperAccaPageDirectionB />;
  if (direction === "C") return <SuperAccaPageDirectionC />;
  return <SuperAccaPageDirectionA />;
}

function SuperAccaPageDirectionA(): JSX.Element {
  const vm = useSuperAccaToday();
  const {
    slip,
    fixture,
    disciplinary,
    territorial,
    playerPerformance,
    scorelineMatrix,
    derivedMarkets,
    sharpMoney,
    loading,
    error,
  } = vm;

  const [category, setCategory] = useState<MarketCategoryFilter>("ALL");

  const {
    probabilityPicks: doctrineProbPicks,
    valuePicks: doctrineValuePicks,
    substrateEmpty,
    substrateWeek,
  } = useDoctrinePicks({ t1HasPicks: !!slip });

  // Phase G — fire `super_acca_view` once per day per user.
  useConversionTriggerPump(
    slip ? "super_acca_view" : null,
    slip ? { slip_id: slip.id, pick_date: slip.pick_date } : {},
  );

  const categoryTallies = useMemo(() => {
    if (!slip) return null;
    const t: Record<string, number> = {
      OUTCOME: 0,
      DISCIPLINE: 0,
      TERRITORY: 0,
      PLAYER: 0,
    };
    for (const leg of slip.legs) {
      if (leg.category) t[leg.category] = (t[leg.category] ?? 0) + 1;
    }
    return t;
  }, [slip]);

  const publishedLabel =
    slip?.status === "published"
      ? "PUBLISHED · 11:30 GMT"
      : slip?.status === "abstained"
        ? "ABSTAINED · TIER FLOOR NOT MET"
        : "AWAITING TODAY'S BUILD";

  return (
    <div
      className="sa-root"
      data-screen-label="00 SUPER ACCA"
      style={{
        background: "var(--sa-bg-base)",
        minHeight: "var(--vh-full)",
        color: "var(--sa-fg-primary)",
        fontFamily: "var(--sa-font-body)",
      }}
    >
      <MatchCommandHeader
        fixture={fixture}
        sharp={sharpMoney}
        publishedLabel={publishedLabel}
      />

      <div style={{ padding: "32px 40px 72px", maxWidth: 1280, margin: "0 auto" }}>
        {error ? (
          <div
            style={{
              padding: 14,
              border: "1px solid var(--sa-signal-red)",
              background: "var(--sa-signal-red-dim)",
              color: "var(--sa-signal-red)",
              borderRadius: 4,
              marginBottom: 18,
              fontFamily: "var(--sa-font-mono)",
              fontSize: 12,
            }}
          >
            <strong>Could not load Super Acca.</strong> {error}
          </div>
        ) : null}

        {loading ? (
          <div
            style={{
              padding: 40,
              border: "1px solid var(--sa-hairline)",
              background: "var(--sa-bg-panel)",
              borderRadius: 8,
              fontFamily: "var(--sa-font-mono)",
              fontSize: 12,
              color: "var(--sa-fg-secondary)",
              letterSpacing: "0.16em",
            }}
          >
            LOADING TODAY'S COMMAND CENTRE…
          </div>
        ) : !slip ? (
          <>
            <EmptyState />
            <DoctrineSection
              probabilityPicks={doctrineProbPicks}
              valuePicks={doctrineValuePicks}
              isFullFallback
              substrateEmpty={substrateEmpty}
              substrateWeek={substrateWeek}
            />
          </>
        ) : (
          <>
            <div
              style={{
                fontFamily: "var(--sa-font-mono)",
                fontSize: 10.5,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: "var(--sa-accent-saffron)",
              }}
            >
              Intelligence Verdict
            </div>
            <div style={{ marginTop: 14 }}>
              <TierStrip slip={slip} />
              <IntelligenceVerdict slip={slip} />
            </div>

            <IntelligenceStack slip={slip} />

            <MarketCategorySelector active={category} onChange={setCategory} />
            <div style={{ marginTop: 18 }}>
              {category === "ALL" ? (
                <CategoryAllPanel tallies={categoryTallies} />
              ) : null}
              {category === "OUTCOME" ? (
                <CategoryOutcomePanel />
              ) : null}
              {category === "DISCIPLINE" ? (
                <RefereeDNAPanel data={disciplinary} />
              ) : null}
              {category === "TERRITORY" ? (
                <CornersIntelPanel data={territorial} />
              ) : null}
              {category === "PLAYER" ? (
                <PlayerDNAPanel data={playerPerformance} />
              ) : null}
            </div>

            <ScorelineMatrix matrix={scorelineMatrix} derived={derivedMarkets} />

            <AdversarialReview slip={slip} />
            <AgentDebate slip={slip} />
            <BuildYourOwn slip={slip} />
          </>
        )}

        <footer
          style={{
            marginTop: 40,
            padding: "16px 0",
            borderTop: "1px solid var(--sa-hairline)",
            fontFamily: "var(--sa-font-mono)",
            fontSize: 11,
            color: "var(--sa-fg-muted)",
            letterSpacing: "0.16em",
            textAlign: "center",
          }}
        >
          STRIKZA NEVER PLACES BETS · YOU COPY ANY SLIP TO YOUR BOOKMAKER
          MANUALLY
        </footer>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div
      style={{
        padding: 40,
        border: "1px solid var(--sa-hairline)",
        background: "var(--sa-bg-panel)",
        borderRadius: 8,
      }}
    >
      <div
        style={{
          fontFamily: "var(--sa-font-mono)",
          fontSize: 10.5,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          color: "var(--sa-accent-saffron)",
        }}
      >
        TODAY'S BUILD
      </div>
      <h2
        style={{
          fontFamily: "var(--sa-font-display)",
          fontSize: 28,
          fontWeight: 600,
          marginTop: 12,
          color: "var(--sa-fg-primary)",
        }}
      >
        Today's slip is in adversarial review
      </h2>
      <p
        style={{
          fontFamily: "var(--sa-font-body)",
          fontSize: 14,
          color: "var(--sa-fg-secondary)",
          lineHeight: 1.6,
          marginTop: 12,
          maxWidth: 720,
        }}
      >
        The Super Acca only publishes once the slip clears the contradiction
        engine, the adversarial review, and meets the tier confidence floor.
        If conviction isn't there by 11:30 GMT, no slip is published —
        STRIKZA never invents a fabricated tip. Check back later today, or
        review yesterday's settled slip in your archive.
      </p>
    </div>
  );
}

function CategoryAllPanel({
  tallies,
}: {
  tallies: Record<string, number> | null;
}) {
  if (!tallies) return null;
  const items = [
    { id: "OUTCOME", label: "OUTCOME", color: "#5D9ABF", glyph: "◆" },
    { id: "DISCIPLINE", label: "DISCIPLINE", color: "#BF5D5D", glyph: "◐" },
    { id: "TERRITORY", label: "TERRITORY", color: "#5DBF8A", glyph: "◇" },
    { id: "PLAYER", label: "PLAYER", color: "#D4A843", glyph: "★" },
  ];
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        gap: 14,
      }}
    >
      {items.map((c) => (
        <div
          key={c.id}
          style={{
            padding: 18,
            background: "var(--sa-bg-panel)",
            border: "1px solid var(--sa-hairline)",
            borderRadius: 4,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span
              style={{
                fontFamily: "var(--sa-font-mono)",
                fontSize: 10,
                color: c.color,
                letterSpacing: "0.18em",
              }}
            >
              {c.glyph} {c.label}
            </span>
            <span
              style={{
                fontFamily: "var(--sa-font-stat)",
                fontSize: 22,
                color: c.color,
              }}
            >
              {tallies[c.id] ?? 0}
            </span>
          </div>
          <div
            style={{
              fontFamily: "var(--sa-font-mono)",
              fontSize: 10.5,
              color: "var(--sa-fg-muted)",
              marginTop: 8,
            }}
          >
            LEGS IN SLIP
          </div>
        </div>
      ))}
    </div>
  );
}

function CategoryOutcomePanel() {
  return (
    <div
      style={{
        padding: 22,
        background: "var(--sa-bg-panel)",
        border: "1px solid var(--sa-hairline)",
        borderRadius: 4,
      }}
    >
      <div
        style={{
          fontFamily: "var(--sa-font-mono)",
          fontSize: 10.5,
          letterSpacing: "0.22em",
          color: "var(--sa-accent-saffron)",
          textTransform: "uppercase",
        }}
      >
        Outcome Markets · derived from Scoreline Matrix
      </div>
      <div
        style={{
          marginTop: 14,
          fontFamily: "var(--sa-font-body)",
          fontSize: 13.5,
          color: "var(--sa-fg-secondary)",
          lineHeight: 1.7,
        }}
      >
        The Poisson scoreline matrix feeds Match Winner, Asian Handicap,
        Total Goals, BTTS, Correct Score, and HT/FT simultaneously — see the
        matrix below for the full distribution.
      </div>
    </div>
  );
}
